
/**
 * Enhanced error logging utility to help track down page refresh issues
 */

interface ErrorLog {
  type: 'error' | 'unhandledRejection' | 'reactError' | 'tabSwitch' | 'authChange' | 'networkError';
  message: string;
  stack?: string;
  timestamp: string;
  userAgent: string;
  url: string;
  additionalInfo?: any;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private maxLogs = 100;
  private tabSwitchCount = 0;
  private lastTabVisible = true;

  constructor() {
    this.setupGlobalErrorHandlers();
    this.setupTabVisibilityTracking();
    this.setupBeforeUnloadTracking();
  }

  private setupGlobalErrorHandlers() {
    // Catch unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'error',
        message: event.error?.message || event.message || 'Unknown error',
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        additionalInfo: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          tabSwitchCount: this.tabSwitchCount
        }
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'unhandledRejection',
        message: event.reason?.message || String(event.reason) || 'Unhandled promise rejection',
        stack: event.reason?.stack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        additionalInfo: {
          reason: event.reason,
          tabSwitchCount: this.tabSwitchCount
        }
      });
    });

    // Catch network errors
    window.addEventListener('offline', () => {
      this.logError({
        type: 'networkError',
        message: 'Network went offline',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        additionalInfo: {
          tabSwitchCount: this.tabSwitchCount
        }
      });
    });

    window.addEventListener('online', () => {
      this.logError({
        type: 'networkError',
        message: 'Network came back online',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        additionalInfo: {
          tabSwitchCount: this.tabSwitchCount
        }
      });
    });
  }

  private setupTabVisibilityTracking() {
    document.addEventListener('visibilitychange', () => {
      const isVisible = !document.hidden;
      const wasVisible = this.lastTabVisible;
      this.lastTabVisible = isVisible;
      
      if (!wasVisible && isVisible) {
        // Tab became visible (user switched back)
        this.tabSwitchCount++;
        console.log(`👁️ [TAB-SWITCH] Tab became visible (switch #${this.tabSwitchCount}) at ${new Date().toISOString()}`);
        
        this.logError({
          type: 'tabSwitch',
          message: `Tab became visible (switch #${this.tabSwitchCount})`,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
          additionalInfo: {
            switchCount: this.tabSwitchCount,
            previouslyVisible: wasVisible,
            currentlyVisible: isVisible
          }
        });
      } else if (wasVisible && !isVisible) {
        // Tab became hidden (user switched away)
        console.log(`👁️ [TAB-SWITCH] Tab became hidden at ${new Date().toISOString()}`);
      }
    });

    // Track focus/blur events as well
    window.addEventListener('focus', () => {
      console.log(`🔍 [FOCUS] Window gained focus at ${new Date().toISOString()}`);
    });

    window.addEventListener('blur', () => {
      console.log(`🔍 [BLUR] Window lost focus at ${new Date().toISOString()}`);
    });
  }

  private setupBeforeUnloadTracking() {
    window.addEventListener('beforeunload', (event) => {
      console.log(`🚪 [BEFORE-UNLOAD] Page about to unload at ${new Date().toISOString()}`);
      this.logError({
        type: 'error',
        message: 'Page about to unload',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        additionalInfo: {
          tabSwitchCount: this.tabSwitchCount,
          reason: 'beforeunload'
        }
      });
    });

    // Track page load/reload
    window.addEventListener('load', () => {
      console.log(`🔄 [PAGE-LOAD] Page loaded at ${new Date().toISOString()}`);
    });
  }

  private logError(errorLog: ErrorLog) {
    this.logs.push(errorLog);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Log to console with appropriate level
    const logLevel = errorLog.type === 'unhandledRejection' ? 'warn' : 'error';
    console[logLevel](`🚨 [${errorLog.type.toUpperCase()}]`, errorLog);

    // Check for specific patterns that might cause page refreshes
    if (errorLog.message.includes('Rendered more hooks')) {
      console.error('🚨 [CRITICAL] Hook rule violation detected - this is likely causing page refreshes!');
    }

    if (errorLog.message.includes('Authentication') || errorLog.message.includes('auth')) {
      console.error('🚨 [AUTH-ERROR] Authentication error detected during tab switch!');
    }

    if (errorLog.type === 'tabSwitch' && this.tabSwitchCount > 5) {
      console.warn('⚠️ [TAB-SWITCH] High number of tab switches detected - monitoring for refresh patterns');
    }
  }

  public getRecentErrors(count = 20): ErrorLog[] {
    return this.logs.slice(-count);
  }

  public getTabSwitchCount(): number {
    return this.tabSwitchCount;
  }

  public clearLogs(): void {
    this.logs = [];
    this.tabSwitchCount = 0;
  }
}

// Create singleton instance
export const errorLogger = new ErrorLogger();
